(() => {
  if (customElements.get('ad-review-carousel')) return;

  class ADReviewCarousel extends HTMLElement {
    connectedCallback() {
      Promise.resolve().then(() => this.initialize());
    }

    disconnectedCallback() {
      this.restoreFallback();
    }

    restoreFallback() {
      this.stopTimer();
      this.observer?.disconnect();
      this.events?.abort();
      this.ready = false;
      this.hidden = true;
      if (this.fallback) this.fallback.hidden = false;
    }

    initialize() {
      if (this.ready || !this.isConnected) return;
      this.fallback = document.getElementById(this.dataset.reviewFallbackId);
      if (!this.fallback || getComputedStyle(this).getPropertyValue('--customer-review-enhancement').trim() !== 'ready') return;
      try {
        this.initializeCarousel();
      } catch (error) {
        this.restoreFallback();
      }
    }

    initializeCarousel() {
      const source = this.querySelector('[data-review-source]');
      this.track = this.querySelector('[data-review-track]');
      this.viewport = this.querySelector('[data-review-viewport]');
      if (!source || !this.track || !this.viewport) return;

      this.events = new AbortController();
      const options = { signal: this.events.signal };
      const seen = new Set();
      const reviews = [...source.content.querySelectorAll('.jdgm-carousel-item')].filter((review) => {
        const id = review.dataset.reviewId;
        const stars = review.querySelector('.jdgm-carousel-item__review-rating');
        const body = review.querySelector('.jdgm-carousel-item__review-body');
        if (!id || seen.has(id) || !body?.textContent.trim() || stars?.querySelectorAll('.jdgm--on').length !== 5 || stars.querySelector('.jdgm--half')) return false;
        seen.add(id);
        return true;
      });
      if (!reviews.length) return;

      this.index = 0;
      this.paused = false;
      this.hovered = false;
      this.visible = false;
      this.expanded = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.counter = this.querySelector('[data-review-counter]');
      this.status = this.querySelector('[data-review-status]');
      this.pauseButton = this.querySelector('[data-review-pause]');
      this.cards = reviews.map((review, index) => this.makeCard(review, index));
      this.track.replaceChildren(...this.cards);
      this.dataset.reviewCount = String(this.cards.length);
      this.show(0, false);

      this.querySelector('[data-review-previous]').addEventListener('click', () => this.navigate(-1), options);
      this.querySelector('[data-review-next]').addEventListener('click', () => this.navigate(1), options);
      this.pauseButton.addEventListener('click', () => {
        this.paused = !this.paused;
        this.updatePauseButton();
        this.schedule();
      }, options);
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.stopTimer(); }
      }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => this.stopTimer(), options);
      this.addEventListener('focusout', () => Promise.resolve().then(() => this.schedule()), options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => { this.updatePauseButton(); this.schedule(); }, options);
      this.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigate(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);

      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' || event.target.closest('button, a')) return;
        this.swipe = { x: event.clientX, y: event.clientY };
        this.stopTimer();
      }, options);
      this.viewport.addEventListener('pointerup', (event) => {
        if (!this.swipe) return;
        const x = event.clientX - this.swipe.x;
        const y = event.clientY - this.swipe.y;
        this.swipe = null;
        if (Math.abs(x) > 50 && Math.abs(x) > Math.abs(y) * 1.5) this.navigate(x < 0 ? 1 : -1);
        else this.schedule();
      }, options);
      this.viewport.addEventListener('pointercancel', () => { this.swipe = null; this.schedule(); }, options);

      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .35;
        this.schedule();
      }, { threshold: [0, .35] });
      this.observer.observe(this.viewport);
      this.querySelector('[data-review-controls]').hidden = this.cards.length < 2;
      this.updatePauseButton();
      this.ready = true;
      this.hidden = false;
      this.fallback.hidden = true;
      this.schedule();
    }

    makeCard(source, index) {
      const card = document.createElement('article');
      card.className = 'ad-reviews__card';
      card.dataset.reviewId = source.dataset.reviewId;
      card.setAttribute('role', 'group');
      card.setAttribute('aria-label', this.reviewLabel(index));
      const stars = document.createElement('div');
      stars.className = 'ad-reviews__stars';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', this.dataset.starLabel);
      stars.textContent = '★★★★★';
      card.append(stars);
      const title = source.querySelector('.jdgm-carousel-item__review-title')?.textContent.trim();
      if (title) {
        const heading = document.createElement('h3');
        heading.className = 'ad-reviews__review-title';
        heading.textContent = title;
        card.append(heading);
      }

      const body = document.createElement('div');
      body.className = 'ad-reviews__body';
      const full = document.createElement('div');
      full.className = 'ad-reviews__full';
      full.id = `${this.id}-body-${index}`;
      this.copyTextMarkup(source.querySelector('.jdgm-carousel-item__review-body'), full);
      const text = full.textContent.replace(/\s+/g, ' ').trim();
      if (text.length > 240) {
        const excerpt = document.createElement('p');
        excerpt.className = 'ad-reviews__excerpt';
        const boundary = text.lastIndexOf(' ', 220);
        excerpt.textContent = `${text.slice(0, boundary > 150 ? boundary : 220)}…`;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ad-reviews__read-more';
        button.textContent = this.dataset.readMore;
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', full.id);
        full.hidden = true;
        body.append(excerpt, full, button);
        button.addEventListener('click', () => {
          const expand = button.getAttribute('aria-expanded') !== 'true';
          button.setAttribute('aria-expanded', String(expand));
          button.textContent = expand ? this.dataset.readLess : this.dataset.readMore;
          full.hidden = !expand;
          excerpt.hidden = expand;
          this.expanded = expand;
          this.schedule();
        }, { signal: this.events.signal });
      } else body.append(full);
      card.append(body);

      const author = source.querySelector('.jdgm-carousel-item__reviewer-name')?.textContent.trim();
      if (author) {
        const name = document.createElement('p');
        name.className = 'ad-reviews__author';
        name.textContent = author;
        card.append(name);
      }
      const originalLink = source.querySelector('a.jdgm-carousel-item__product');
      const productName = originalLink?.querySelector('.jdgm-carousel-item__product-title')?.textContent.trim();
      if (originalLink && productName) {
        let url;
        try { url = new URL(originalLink.getAttribute('href'), window.location.origin); } catch (error) {}
        if (url && url.origin === window.location.origin && url.pathname.startsWith('/products/')) {
          const link = document.createElement('a');
          link.className = 'ad-reviews__product';
          link.href = `${url.pathname}${url.search}${url.hash}`;
          link.textContent = productName;
          card.append(link);
        }
      }
      return card;
    }

    copyTextMarkup(source, target) {
      const allowed = new Set(['P', 'BR', 'STRONG', 'EM', 'B', 'I', 'U', 'UL', 'OL', 'LI', 'BLOCKQUOTE']);
      for (const node of source.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) target.append(document.createTextNode(node.textContent));
        else if (node.nodeType === Node.ELEMENT_NODE && !['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT'].includes(node.tagName)) {
          const destination = allowed.has(node.tagName) ? document.createElement(node.tagName.toLowerCase()) : target;
          this.copyTextMarkup(node, destination);
          if (destination !== target) target.append(destination);
        }
      }
    }

    reviewLabel(index) {
      return this.dataset.reviewLabel.replace('__index__', String(index + 1)).replace('__total__', String(this.cards?.length || this.querySelector('[data-review-source]').content.querySelectorAll('.jdgm-carousel-item').length));
    }

    show(index, announce) {
      const total = this.cards.length;
      this.index = (index + total) % total;
      const previous = (this.index + total - 1) % total;
      const next = (this.index + 1) % total;
      this.cards.forEach((card, position) => {
        const active = position === this.index;
        card.classList.toggle('is-active', active);
        card.classList.toggle('is-previous', total > 2 && position === previous);
        card.classList.toggle('is-next', total > 1 && position === next);
        card.setAttribute('aria-hidden', String(!active));
        card.inert = !active;
        card.setAttribute('aria-label', this.reviewLabel(position));
        if (!active) {
          const button = card.querySelector('.ad-reviews__read-more');
          if (button) {
            button.setAttribute('aria-expanded', 'false');
            button.textContent = this.dataset.readMore;
            card.querySelector('.ad-reviews__full').hidden = true;
            card.querySelector('.ad-reviews__excerpt').hidden = false;
          }
        }
      });
      this.expanded = false;
      this.counter.textContent = `${this.index + 1} / ${total}`;
      if (announce) this.status.textContent = this.reviewLabel(this.index);
      this.schedule();
    }

    navigate(direction, announce = true) {
      if (this.cards.length < 2) return;
      const focusedCard = document.activeElement.closest('.ad-reviews__card');
      if (focusedCard && this.contains(focusedCard)) this.querySelector(direction > 0 ? '[data-review-next]' : '[data-review-previous]').focus({ preventScroll: true });
      this.style.setProperty('--ad-review-enter-x', direction > 0 ? '12px' : '-12px');
      this.show(this.index + direction, announce);
    }

    updatePauseButton() {
      this.pauseButton.disabled = this.motion.matches;
      this.pauseButton.textContent = this.motion.matches ? this.dataset.motionPaused : (this.paused ? this.dataset.play : this.dataset.pause);
    }

    stopTimer() {
      clearTimeout(this.timer);
      this.timer = null;
    }

    schedule() {
      this.stopTimer();
      if (!this.ready || !this.isConnected || this.cards.length < 2 || !this.visible || this.paused || this.hovered || this.expanded || this.motion.matches || document.hidden || this.contains(document.activeElement)) return;
      this.timer = setTimeout(() => this.navigate(1, false), 7000);
    }
  }

  customElements.define('ad-review-carousel', ADReviewCarousel);
})();
